import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Television, CircleNotch } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { getHandoff } from "@/lib/api";

// TV-side of Couch Handoff: enter the 4-digit code from your phone to resume the title.
export default function TvHandoff() {
  const navigate = useNavigate();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  const setDigit = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError("");
    if (v && i < 3) refs[i + 1].current?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  const submit = async () => {
    const code = digits.join("");
    if (code.length !== 4) {
      setError("Enter the 4-digit code shown on your phone.");
      return;
    }
    setLoading(true);
    try {
      const { video_id, position } = await getHandoff(code);
      navigate(`/watch/${video_id}?t=${position || 0}`);
    } catch {
      setError("That code is invalid or expired. Generate a fresh one on your phone.");
      setDigits(["", "", "", ""]);
      refs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Send to TV · ReelVault</title>
      </Helmet>
      <Header />
      <main className="mx-auto max-w-md px-5 py-20 text-center rv-fade" data-testid="tv-handoff">
        <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-2xl bg-amber/10 border border-amber/30">
          <Television size={30} weight="duotone" className="text-amber" />
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-white">Resume on this TV</h1>
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">
          Enter the 4-digit code from your phone
        </p>

        <div className="mt-8 flex justify-center gap-3">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={refs[i]}
              data-testid={`tv-code-${i}`}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              inputMode="numeric"
              maxLength={1}
              autoFocus={i === 0}
              className="h-20 w-16 rounded-xl border border-white/15 bg-[#0A0A0A] text-center font-mono text-4xl text-amber focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber tabular-nums"
            />
          ))}
        </div>

        {error && <p data-testid="tv-handoff-error" className="mt-5 text-crimson text-sm">{error}</p>}

        <button
          data-testid="tv-handoff-submit"
          onClick={submit}
          disabled={loading}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber px-8 py-3 font-mono text-xs uppercase tracking-[0.15em] text-black font-bold hover:bg-amber/80 focus:outline-none focus:ring-2 focus:ring-amber transition-colors duration-300 disabled:opacity-40"
        >
          {loading ? <CircleNotch size={16} className="animate-spin" /> : <Television size={16} weight="fill" />}
          {loading ? "Connecting…" : "Resume Watching"}
        </button>
      </main>
    </div>
  );
}
